/* eslint-disable jsx-a11y/media-has-caption */
import { getObjectAsync } from 'actions/objects-action';
import { Button, Modal } from 'antd';
import LoadingSpinner from 'components/common/loading-spinner';
import { ObjectState, ObjectType } from 'cvat-core-wrapper';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { CombinedState } from 'reducers';
import { filterAnnotations } from 'utils/filter-annotations';
import Text from 'antd/lib/typography/Text';

function extractFilename(str: string): { objectId: string; frameIndex: number; fps: number } {
    const result = str
        .split('/')
        .pop()
        ?.replace(/\.\w+$/, '');

    const [objectId = '', frameIndex = '', fps] = result?.split('_') ?? [];

    return { objectId, frameIndex: parseFloat(frameIndex ?? '0'), fps: parseFloat(fps ?? '24') };
}

function replaceSingleQuotesInBrackets(input: string): string {
    return input.replace(/'/g, '"');
}

function parstJsonWithoutCatch(str: string, fallback: any): any {
    let val: any;
    try {
        val = JSON.parse(replaceSingleQuotesInBrackets(str)) ?? fallback;
    } catch (error) {
        console.log('error ', error);
        val = fallback;
    }
    return val;
}

function frameToSeconds(frameIndex: number, fps: number): number {
    return frameIndex / fps;
}

const VIEW_RAW_VIDEO_MODE = {
    VIEW_FROM_TAG: 'VIEW_FROM_TAG',
    VIEW_FROM_OBJECT: 'VIEW_FROM_OBJECT',
    VIEW_FROM_OBJECT_INAPP: 'VIEW_FROM_OBJECT_INAPP',
};

interface Props {
    frameFilename: string;
}

export default function ViewRawVideoButton(props: Props): JSX.Element {
    const mode = VIEW_RAW_VIDEO_MODE.VIEW_FROM_OBJECT_INAPP;

    const { frameFilename } = props;
    const { objectId, frameIndex, fps } = extractFilename(frameFilename);
    const durationTime = frameToSeconds(frameIndex, fps);

    const [frameTags, setFrameTags] = useState<ObjectState[]>([]);
    const [isOpenDialog, setIsOpenDialog] = useState(false);

    const dispatch = useDispatch();
    const { fetching, initialized, data: objects, error } = useSelector((state: CombinedState) => state.objects);

    const { states, workspace } = useSelector(
        (state: CombinedState) => ({
            highlightedConflict: state.annotation.annotations.highlightedConflict,
            states: state.annotation.annotations.states,
            workspace: state.annotation.workspace,
            labels: state.annotation.job.labels,
        }),
        shallowEqual,
    );

    useEffect(() => {
        setFrameTags(filterAnnotations(states, { workspace, include: [ObjectType.TAG] }));
    }, [states]);

    const videoLink = useMemo(() => {
        const rawAttributes = frameTags?.find((tag) => tag.label.name === 'Link')?.attributes; // *Node link is hardcode for finding video link

        const attributes =
            Object.values(rawAttributes ?? {})
                .map((att) => parstJsonWithoutCatch(att, []))
                .reduce((prev: any[], cur) => [...prev, ...cur], [])
                .find((attribute) => attribute.includes('http')) ?? '';

        return attributes;
    }, [frameTags]);

    useEffect(() => {
        if (mode === VIEW_RAW_VIDEO_MODE.VIEW_FROM_OBJECT_INAPP) return;
        if (objects?.path) {
            window.open(`${objects?.path}#t=${durationTime}`);
        }
    }, [mode, objects]);

    const shouldFetchIfNeed = useCallback(() => {
        if (objects && objectId === objects?.id) {
            return false;
        }

        return true;
    }, [objectId, objects]);

    switch (mode) {
        case VIEW_RAW_VIDEO_MODE.VIEW_FROM_TAG: {
            return (
                <Button
                    type='primary'
                    disabled={!videoLink}
                    onClick={() => {
                        if (videoLink) {
                            window.open(`${videoLink}#t=10`);
                        }
                    }}
                >
                    View raw video
                </Button>
            );
        }
        case VIEW_RAW_VIDEO_MODE.VIEW_FROM_OBJECT: {
            return (
                <Button
                    type='primary'
                    disabled={!objectId}
                    loading={fetching}
                    onClick={() => {
                        if (shouldFetchIfNeed()) {
                            dispatch(getObjectAsync(objectId));
                        } else if (objects?.path) {
                            window.open(objects?.path);
                        }
                    }}
                >
                    View raw video
                </Button>
            );
        }
        case VIEW_RAW_VIDEO_MODE.VIEW_FROM_OBJECT_INAPP: {
            return (
                <>
                    <Button
                        type='primary'
                        disabled={!objectId}
                        onClick={() => {
                            setIsOpenDialog(true);

                            if (shouldFetchIfNeed()) {
                                dispatch(getObjectAsync(objectId));
                            }
                        }}
                    >
                        View raw video
                    </Button>
                    <Modal
                        destroyOnClose
                        width='calc(100vw - 300px)'
                        open={isOpenDialog}
                        closable={false}
                        footer={[
                            <Button
                                key='submit'
                                onClick={() => setIsOpenDialog(false)}
                                className='cvat-filters-modal-submit-button'
                            >
                                Close
                            </Button>,
                        ]}
                        onCancel={() => setIsOpenDialog(false)}
                        styles={{
                            body: {
                                height: 'calc(100vh - 300px)',
                                overflow: 'hidden',
                            },
                        }}
                    >
                        {!fetching && initialized && objects && (
                            <video
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                src={`${objects?.path}#t=${durationTime}`}
                                controls
                            />
                        )}
                        {fetching && <LoadingSpinner />}
                        {error && (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <Text type='secondary' strong>
                                    Error
                                </Text>
                                <Text type='secondary' strong>
                                    {error}
                                </Text>
                            </div>
                        )}
                    </Modal>
                </>
            );
        }

        default: {
            return <div />;
        }
    }
}
